import React from 'react';
import * as Glyphs from './BeelyvIcons';
import type { GlyphProps } from './BeelyvIcons';
import type { HabitIconName, MoodId } from '../../types';

/**
 * BEELYV icon tokens — single source of truth. Every icon in the product is
 * sized, weighted and coloured from here so a change propagates everywhere.
 */
export const iconTokens = {
  size: {
    sm: 16,
    md: 20,
    nav: 22,
    lg: 26
  },
  /** One stroke weight for the whole family (brand board: thin, elegant). */
  stroke: 1.6,
  tone: {
    default: 'text-deep',
    muted: 'text-soft',
    active: 'text-gold',
    accent: 'text-gold-deep',
    onDark: 'text-ondark',
    onDarkActive: 'text-gold',
    disabled: 'text-muted/50'
  }
} as const;
export type IconSize = keyof typeof iconTokens.size;
export type IconTone = keyof typeof iconTokens.tone;

/** Named BEELYV glyphs. */
export const beeGlyphs = {
  workout: Glyphs.WorkoutGlyph,
  nutrition: Glyphs.NutritionGlyph,
  mind: Glyphs.MindGlyph,
  sleep: Glyphs.SleepGlyph,
  water: Glyphs.WaterGlyph,
  community: Glyphs.CommunityGlyph,
  running: Glyphs.RunningGlyph,
  cycling: Glyphs.CyclingGlyph,
  swimming: Glyphs.SwimmingGlyph,
  crossfit: Glyphs.CrossfitGlyph,
  trophy: Glyphs.TrophyGlyph,
  heart: Glyphs.HeartGlyph,
  home: Glyphs.HomeGlyph,
  check: Glyphs.CheckGlyph,
  progress: Glyphs.ProgressGlyph,
  profile: Glyphs.ProfileGlyph,
  hive: Glyphs.HiveGlyph,
  beeReward: Glyphs.BeeRewardGlyph,
  hiveChest: Glyphs.HiveChestGlyph,
  leaf: Glyphs.LeafGlyph,
  streak: Glyphs.StreakGlyph,
  comment: Glyphs.CommentGlyph,
  follow: Glyphs.FollowGlyph,
  following: Glyphs.FollowingGlyph,
  biomarker: Glyphs.BiomarkerGlyph,
  language: Glyphs.LanguageGlyph,
  units: Glyphs.UnitsGlyph,
  notification: Glyphs.NotificationGlyph,
  accessibility: Glyphs.AccessibilityGlyph,
  connection: Glyphs.ConnectionGlyph,
  privacy: Glyphs.PrivacyGlyph,
  security: Glyphs.SecurityGlyph,
  help: Glyphs.HelpGlyph,
  healthSync: Glyphs.HealthSyncGlyph,
  activitySync: Glyphs.ActivitySyncGlyph,
  wearable: Glyphs.WearableGlyph,
  moodDrained: Glyphs.MoodDrainedGlyph,
  moodTired: Glyphs.MoodTiredGlyph,
  moodNeutral: Glyphs.MoodNeutralGlyph,
  moodGood: Glyphs.MoodGoodGlyph,
  moodFocused: Glyphs.MoodFocusedGlyph
} satisfies Record<string, React.ComponentType<GlyphProps>>;
export type BeeGlyphName = keyof typeof beeGlyphs;

/** Daily Check habits → brand glyph. Defined once, reused everywhere. */
export const habitGlyph: Record<HabitIconName, BeeGlyphName> = {
  droplet: 'water',
  activity: 'workout',
  brain: 'mind',
  salad: 'nutrition',
  moon: 'sleep',
  pulse: 'heart'
};

/**
 * Connected-service providers → category glyph. Third-party logos are never
 * redrawn; each provider shows the kind of data it would contribute.
 */
export const serviceGlyph: Record<string, BeeGlyphName> = {
  'apple-health': 'healthSync',
  'google-health': 'healthSync',
  garmin: 'activitySync',
  strava: 'activitySync',
  wearables: 'wearable',
  other: 'connection'
};

/** Mood options → brand glyph (replaces emoji). */
export const moodGlyph: Record<MoodId, BeeGlyphName> = {
  drained: 'moodDrained',
  tired: 'moodTired',
  neutral: 'moodNeutral',
  good: 'moodGood',
  focused: 'moodFocused'
};
type IconSource = {
  name: BeeGlyphName;
  as?: never;
} | {
  as: React.ComponentType<GlyphProps>;
  name?: never;
};
type IconProps = IconSource & {
  size?: IconSize | number;
  tone?: IconTone | 'inherit';
  className?: string;
  /** Pass a label only when the icon stands alone and carries meaning. */
  label?: string;
};

/**
 * <Icon name="water" /> for brand glyphs, or <Icon as={LucideIcon} /> for
 * utility icons (chevrons, close, search…) so they inherit the same size,
 * stroke and tone tokens.
 */
export function Icon({
  name,
  as,
  size = 'md',
  tone = 'inherit',
  className = '',
  label
}: IconProps) {
  const Component = (name ? beeGlyphs[name] : as) as React.ComponentType<GlyphProps>;
  const px = typeof size === 'number' ? size : iconTokens.size[size];
  const toneClass = tone === 'inherit' ? '' : iconTokens.tone[tone];
  return <Component size={px} strokeWidth={iconTokens.stroke} className={`${toneClass} ${className}`.trim()} aria-hidden={label ? undefined : true} aria-label={label} role={label ? 'img' : undefined} data-id="element-820" data-mp="2hxyqrzz16ydq" />;
}
type TileVariant = 'soft' | 'deep' | 'outline' | 'gold';
const tileClass: Record<TileVariant, string> = {
  soft: 'bg-sand-deep text-soft',
  deep: 'bg-deep text-gold',
  outline: 'border border-gold/45 bg-white/70 text-deep',
  gold: 'bg-gold/15 text-gold-deep'
};

/** Icon + container variant: the rounded tile used by habits, rows and pillars. */
export function IconTile({
  variant = 'soft',
  tileSize = 40,
  className = '',
  ...icon
}: IconProps & {
  variant?: TileVariant;
  tileSize?: number;
}) {
  return <span className={`grid shrink-0 place-items-center rounded-[13px] transition-colors duration-150 ease-brand ${tileClass[variant]} ${className}`} style={{
    width: tileSize,
    height: tileSize
  }} data-id="element-821" data-mp="1fj86nnkhc0tx">
      <Icon {...icon as IconProps} data-id="element-822" data-mp="3m7qlyr2e7vt2" />
    </span>;
}